import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ActionColumn from '../components/ActionColumn';
import { useGetUsersQuery, useGetRoleGroupsQuery } from '../features/graph/services/graphApi';
import CopyRoles from '../features/users/components/CopyRoles';
import SubmitChanges from '../features/users/components/SubmitChanges';
import UserGroupMembership from '../features/users/components/UserGroupMembership';
import UserInfo from '../features/users/components/UserInfo';
import UserSelect from '../features/users/components/UserSelect';
import { clearPendingRoles } from '../features/users/services/roleMembershipSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';

export default function Users() {
  const { userId } = useParams<{ userId: string }>(); // Get userId from URL

  // Graph API queries
  const { data: userData, isFetching: userDataIsFetching } = useGetUsersQuery();
  const { data: roleGroupData } = useGetRoleGroupsQuery();
  const user = userData?.find((user) => user.id === userId);
  const dispatch = useAppDispatch();
  // Both view toggles are lifted here so they can sit together as one switch group in
  // the header: showTransitive drives the membership panels, showDisabled the user search.
  const [showTransitive, setShowTransitive] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);

  // dispatch clearPendingRoles when userId changes
  useEffect(() => {
    dispatch(clearPendingRoles());
  }, [userId, dispatch]);

  //const allGroups = [...(groupMembership || []), ...(transitiveGroupMembership || [])];

  return (
    // Bound the page to the viewport (minus the sticky nav) and lay it out as a column
    // so the body flex-fills. The two membership panels then scroll internally instead
    // of growing the page tall — mirrors the Roles page.
    <div className="w-full px-6 flex flex-col h-[calc(100vh-6.25rem)]">
      <div className="flex-none">
        <div className="flex flex-row flex-wrap items-center gap-4 bg-slate-50 dark:bg-gray-800 rounded-lg w-full px-4 py-2">
          {/* left: the search controls. The view toggles sit beneath the user search,
              formatted like the ACLs page (label left, switch at the right end). The
              "add a role" search now lives inside the Role membership panel below. */}
          <div className="flex flex-row gap-4 shrink-0">
            {userDataIsFetching ? (
              'Loading...'
            ) : (
              <div className="w-72">
                <UserSelect users={userData} selectedId={userId} showDisabled={showDisabled} />
                {/* Switch on the left, label after it. showDisabled filters the user
                    search; showTransitive expands the panels below to include inherited
                    (nested) memberships. */}
                <label className="cursor-pointer label justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-primary toggle-sm"
                    checked={showDisabled}
                    onChange={() => setShowDisabled(!showDisabled)}
                  />
                  <span className="text-xs">Show disabled users</span>
                </label>
                <label className="cursor-pointer label justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-primary toggle-sm"
                    checked={showTransitive}
                    onChange={() => setShowTransitive(!showTransitive)}
                  />
                  <span className="text-xs">Show transitive memberships</span>
                </label>
              </div>
            )}
          </div>
          {/* middle: user details, grown into the space between search and the action
                (mirrors RoleInfo on the Roles page) */}
          <div className="flex-1 min-w-0">
            <UserInfo user={user} />
          </div>
          <ActionColumn>
            <CopyRoles users={userData} user={user} />
            <SubmitChanges user={user} />
          </ActionColumn>
        </div>
      </div>
      <div className="flex-1 min-h-0 pt-4 pb-4">
        <UserGroupMembership
          user={user}
          showTransitive={showTransitive}
          roleOptions={roleGroupData}
        />
      </div>
    </div>
  );
}
